import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef } from '@angular/core';
import { NgZone } from '@angular/core';
import { ToastMessage, ToastService } from '../../../../core/toast.service';
import { Mutex } from 'async-mutex';
import { Subscription } from 'rxjs';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './toast.component.html',
  styleUrl: './toast.component.scss',
})
export class ToastComponent implements OnInit, OnDestroy {
  toastMessage: ToastMessage | null = null;

  private toastMessageQueue: (ToastMessage | null)[] = [];
  private mutex = new Mutex();
  private autoCloseTimer?: ReturnType<typeof setTimeout>;
  private autoCloseInterval?: ReturnType<typeof setInterval>;
  private readonly autoCloseDuration = 5000;
  private autoCloseExpiresAt?: number; // absolute timestamp when toast should close
  progress = 100; // for non-error toasts
  private sub?: Subscription;

  constructor(private toastService: ToastService, private cdr: ChangeDetectorRef, private ngZone: NgZone) {}

  ngOnInit(): void {
    this.sub = this.toastService.message$.subscribe((toast) => {
      this.mutex.runExclusive(() => {
        if (this.toastMessage == null) {
          this.toastMessage = toast;
          this.startAutoCloseIfNeeded();
        } else {
          this.toastMessageQueue.push(toast);
        }
      });
    });

  }

  ngOnDestroy(): void {
    this.clearAutoCloseTimer();
    this.sub?.unsubscribe();
  }

  closeToast() {
    this.mutex.runExclusive(() => {
      this.clearAutoCloseTimer();

      if (this.toastMessageQueue.length > 0) {
        this.toastMessage = this.toastMessageQueue.shift() || null;
        this.startAutoCloseIfNeeded();
      } else {
        this.toastMessage = null;
      }
    }).finally(() => {
      // Ensure UI updates even if closure happens from non-Angular timers
      this.cdr.detectChanges();
    });
  }

  private startAutoCloseIfNeeded() {
    this.clearAutoCloseTimer();
    // Auto-close for non-error toasts (success, info) with progress bar
    if (this.toastMessage && this.toastMessage.type !== 'warn') {
      const start = Date.now();
      this.autoCloseExpiresAt = start + this.autoCloseDuration;
      this.progress = 100;
      this.autoCloseTimer = setTimeout(
        () => this.closeToast(),
        this.autoCloseDuration
      );
      this.autoCloseInterval = setInterval(() => {
        const now = Date.now();
        const remaining = Math.max((this.autoCloseExpiresAt ?? now) - now, 0);
        this.progress = Math.round((remaining / this.autoCloseDuration) * 100);
        if (remaining <= 0) {
          this.clearAutoCloseTimer();
        }
      }, 100);
    }
  }

  private clearAutoCloseTimer() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = undefined;
    }
    if (this.autoCloseInterval) {
      clearInterval(this.autoCloseInterval);
      this.autoCloseInterval = undefined;
    }
    this.autoCloseExpiresAt = undefined;
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibilityChange() {
    this.handleVisibilityOrFocus();
  }

  @HostListener('window:focus')
  onWindowFocus() {
    this.handleVisibilityOrFocus();
  }

  @HostListener('window:pageshow')
  onWindowPageShow() {
    this.handleVisibilityOrFocus();
  }

  private handleVisibilityOrFocus() {
    this.ngZone.run(() => {
      // If a non-warn toast is active, close it if its deadline passed while hidden
      if (this.toastMessage && this.toastMessage.type !== 'warn') {
        const now = Date.now();
        if (this.autoCloseExpiresAt && now >= this.autoCloseExpiresAt) {
          this.closeToast();
        } else if (this.autoCloseExpiresAt) {
          // Re-sync the remaining timeout (Firefox may clamp/paus timers while hidden)
          const remaining = Math.max(this.autoCloseExpiresAt - now, 0);
          if (this.autoCloseTimer) {
            clearTimeout(this.autoCloseTimer);
          }
          this.autoCloseTimer = setTimeout(() => this.closeToast(), remaining);
          // Update progress immediately to reflect correct remaining time
          this.progress = Math.round((remaining / this.autoCloseDuration) * 100);
          this.cdr.detectChanges();
        }
      }
    });
  }
}
